import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Manufacturer, ManufacturerForm, ManufacturerListMeta, ManufacturerReorderDirection } from "../types";
import { initialManufacturerForm, initialState } from "./initialState";

export const sliceKey = "manufacturerState";

type ManufacturerListSuccessPayload = {
  data: Manufacturer[];
  meta: ManufacturerListMeta;
};

const manufacturerSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getManufacturersList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getManufacturersListSuccess(state, action: PayloadAction<ManufacturerListSuccessPayload>) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getManufacturersListFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(state, action: PayloadAction<ManufacturerListSuccessPayload>) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setFormField(
      state,
      action: PayloadAction<{ name: keyof ManufacturerForm; value: string }>
    ) {
      state.form[action.payload.name] = action.payload.value;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
    },

    resetForm(state) {
      state.form = initialManufacturerForm;
    },

    createManufacturer(state, _action: PayloadAction<ManufacturerForm>) {
      state.loading = true;
    },

    createManufacturerSuccess(state, action: PayloadAction<Manufacturer>) {
      state.loading = false;
      state.list.push(action.payload);
      state.form = initialManufacturerForm;
    },

    createManufacturerFailure(state) {
      state.loading = false;
    },

    setSelectedId(state, action: PayloadAction<number>) {
      state.selectedId = action.payload;
    },

    deleteManufacturer(state, action: PayloadAction<number>) {
      state.loading = true;
      state.selectedId = action.payload;
    },

    deleteManufacturerSuccess(state, action: PayloadAction<number>) {
      state.loading = false;
      state.list = state.list.filter(m => m.menufecturerID !== action.payload);
      state.selectedId = 0;
    },

    deleteManufacturerFailure(state) {
      state.loading = false;
    },

    reorderManufacturer(
      state,
      action: PayloadAction<{ id: number; direction: ManufacturerReorderDirection }>
    ) {
      state.reordering = true;
      state.error = null;
      state.selectedId = action.payload.id;
    },

    reorderManufacturerSuccess(state) {
      state.reordering = false;
    },

    reorderManufacturerFailure(state, action: PayloadAction<string>) {
      state.reordering = false;
      if (action.payload) {
        state.error = action.payload;
      }
    },
  },
});

export const { actions: manufacturerActions, reducer } = manufacturerSlice;
